import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useRealtimeTranscription } from "../../hooks/useRealtimeTranscription";

export interface DictationActions {
  connect: () => void;
  startDictation: () => boolean;
  stopDictation: () => void;
  cancelDictation: () => void;
  selectMicDevice: (deviceId: string) => void;
}

const DictationActionsContext = createContext<DictationActions | null>(null);

interface DictationProviderProps {
  userId: string | null;
  token: string | null;
  onFinalTranscript: (transcript: string) => void | Promise<void>;
  children: ReactNode;
}

/**
 * Mounts the dictation pipeline once (sole writer of dictationSessionAtom)
 * and exposes lifecycle actions to descendants via context. State is read
 * from the atom by consumers — this context only carries verbs.
 */
export function DictationProvider({
  userId,
  token,
  onFinalTranscript,
  children,
}: DictationProviderProps) {
  const rtt = useRealtimeTranscription({
    userId: userId ?? "",
    token: token ?? "",
    onFinalTranscript,
  });

  const actions = useMemo<DictationActions>(
    () => ({
      connect: rtt.connect,
      startDictation: rtt.start,
      stopDictation: rtt.stop,
      cancelDictation: rtt.cancel,
      selectMicDevice: rtt.selectMicDevice,
    }),
    [rtt.connect, rtt.start, rtt.stop, rtt.cancel, rtt.selectMicDevice],
  );

  return (
    <DictationActionsContext.Provider value={actions}>{children}</DictationActionsContext.Provider>
  );
}

export function useDictationActions(): DictationActions {
  const ctx = useContext(DictationActionsContext);
  if (!ctx) {
    throw new Error("useDictationActions must be used inside <DictationProvider>");
  }
  return ctx;
}
